export const SUPPORT_LEVELS = Object.freeze({
  INDEPENDENTE: 'independente',
  PISTA_LEVE: 'pista_leve',
  AJUDA_MODERADA: 'ajuda_moderada',
  AJUDA_INTENSA: 'ajuda_intensa',
});

export const SUPPORT_OPTIONS = Object.freeze([
  { value: SUPPORT_LEVELS.INDEPENDENTE, label: 'Independente' },
  { value: SUPPORT_LEVELS.PISTA_LEVE, label: 'Pista leve' },
  { value: SUPPORT_LEVELS.AJUDA_MODERADA, label: 'Ajuda moderada' },
  { value: SUPPORT_LEVELS.AJUDA_INTENSA, label: 'Ajuda intensa' },
]);

export const GENERALIZATION_CONTEXTS = Object.freeze([
  { value: 'outro_material', label: 'Outro material' },
  { value: 'outra_pessoa', label: 'Outra pessoa' },
  { value: 'sala_comum', label: 'Sala comum' },
  { value: 'casa', label: 'Casa' },
  { value: 'ambiente_real', label: 'Ambiente real' },
]);

export const STUDENT_PARTICIPATION_OPTIONS = Object.freeze([
  { value: 'gostou', label: 'Gostou' },
  { value: 'indiferente', label: 'Indiferente' },
  { value: 'nao_gostou', label: 'Não gostou' },
  { value: 'quer_pausa', label: 'Quer pausa' },
]);

export const FAMILY_PARTICIPATION_OPTIONS = Object.freeze([
  { value: 'sim', label: 'Sim' },
  { value: 'parcialmente', label: 'Parcialmente' },
  { value: 'ainda_nao', label: 'Ainda não' },
]);

const STUDENT_PARTICIPATION_VALUES = new Set(STUDENT_PARTICIPATION_OPTIONS.map((item) => item.value));
const FAMILY_PARTICIPATION_VALUES = new Set(FAMILY_PARTICIPATION_OPTIONS.map((item) => item.value));

const GENERALIZATION_ALIASES = Object.freeze({
  outro_material: 'outro_material',
  material_diferente: 'outro_material',
  outra_pessoa: 'outra_pessoa',
  outro_professor: 'outra_pessoa',
  sala_comum: 'sala_comum',
  sala_regular: 'sala_comum',
  casa: 'casa',
  familia: 'casa',
  ambiente_real: 'ambiente_real',
  contexto_real: 'ambiente_real',
});

const SUPPORT_ALIASES = Object.freeze({
  independente: SUPPORT_LEVELS.INDEPENDENTE,
  sem_ajuda: SUPPORT_LEVELS.INDEPENDENTE,
  autonomo: SUPPORT_LEVELS.INDEPENDENTE,
  pista: SUPPORT_LEVELS.PISTA_LEVE,
  pista_leve: SUPPORT_LEVELS.PISTA_LEVE,
  ajuda_leve: SUPPORT_LEVELS.PISTA_LEVE,
  ajuda_moderada: SUPPORT_LEVELS.AJUDA_MODERADA,
  apoio_moderado: SUPPORT_LEVELS.AJUDA_MODERADA,
  ajuda_intensa: SUPPORT_LEVELS.AJUDA_INTENSA,
  apoio_intenso: SUPPORT_LEVELS.AJUDA_INTENSA,
});

const PERFORMANCE_LABELS = Object.freeze({
  conseguiu: 'Conseguiu',
  parcial: 'Parcial',
  nao_conseguiu: 'Não conseguiu',
  'não_conseguiu': 'Não conseguiu',
  'não conseguiu': 'Não conseguiu',
});

function normalizedKey(value) {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
}

function optionalText(value) {
  const text = String(value ?? '').trim();
  return text || '';
}

function participationValue(value, allowed) {
  const normalized = normalizedKey(value);
  return allowed.has(normalized) ? normalized : '';
}

export function normalizeParticipation(value = {}) {
  if (!value || typeof value !== 'object') return null;
  const participation = {
    estudante: participationValue(value.estudante ?? value.student, STUDENT_PARTICIPATION_VALUES),
    familia: participationValue(value.familia ?? value.family, FAMILY_PARTICIPATION_VALUES),
    observacao: optionalText(value.observacao ?? value.observation),
    registradoPor: optionalText(value.registradoPor ?? value.recordedBy),
    registradoEm: optionalText(value.registradoEm ?? value.recordedAt),
    atualizadoPor: optionalText(value.atualizadoPor ?? value.updatedBy),
    atualizadoEm: optionalText(value.atualizadoEm ?? value.updatedAt),
  };
  return participation.estudante || participation.familia || participation.observacao ? participation : null;
}

export function stampParticipation(value, { previous = null, actorId = '', at = '' } = {}) {
  const current = normalizeParticipation(value);
  if (!current) return null;
  const prior = normalizeParticipation(previous);
  const timestamp = optionalText(at) || new Date().toISOString();
  const actor = optionalText(actorId);
  return {
    ...current,
    registradoPor: prior?.registradoPor || actor,
    registradoEm: prior?.registradoEm || timestamp,
    atualizadoPor: actor || prior?.atualizadoPor || prior?.registradoPor || '',
    atualizadoEm: timestamp,
  };
}

function optionLabel(options, value) {
  return options.find((option) => option.value === value)?.label || '';
}

function auditDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(date);
}

export function participationSummary(value) {
  const participation = normalizeParticipation(value);
  if (!participation) return '';
  const parts = [
    participation.estudante ? `estudante: ${optionLabel(STUDENT_PARTICIPATION_OPTIONS, participation.estudante)}` : '',
    participation.familia ? `família: ${optionLabel(FAMILY_PARTICIPATION_OPTIONS, participation.familia)}` : '',
    participation.observacao ? `observação: ${participation.observacao}` : '',
  ].filter(Boolean);
  const audit = [participation.atualizadoPor || participation.registradoPor, auditDate(participation.atualizadoEm || participation.registradoEm)]
    .filter(Boolean).join(' · ');
  return `Participação: ${parts.join('; ')}${audit ? ` (${audit})` : ''}`;
}

export function normalizeSupportLevel(value) {
  return SUPPORT_ALIASES[normalizedKey(value)] || null;
}

export function normalizeGeneralizationContexts(value) {
  const values = Array.isArray(value) ? value : value ? [value] : [];
  return [...new Set(values.map((item) => GENERALIZATION_ALIASES[normalizedKey(item)]).filter(Boolean))];
}

export function generalizationContextLabels(value) {
  const contexts = normalizeGeneralizationContexts(value);
  return contexts.map((context) => GENERALIZATION_CONTEXTS.find((option) => option.value === context)?.label || context);
}

export function generalizationSummary(value) {
  const labels = generalizationContextLabels(value);
  return labels.length ? `Generalização: ${labels.join(', ')}` : '';
}

export function supportLevelLabel(value) {
  const normalized = normalizeSupportLevel(value);
  return SUPPORT_OPTIONS.find((option) => option.value === normalized)?.label || 'Apoio não informado';
}

export function performanceLabel(value) {
  return PERFORMANCE_LABELS[String(value ?? '').trim().toLowerCase()] || 'Resultado não informado';
}

export function normalizeSessionResultItem(item = {}) {
  const attempts = Number(item.tentativas);
  return {
    atividadeId: item.atividadeId || item.activityId || '',
    desempenho: item.desempenho || item.resultado || '',
    score: Number.isFinite(item.score) ? item.score : null,
    apoio: normalizeSupportLevel(item.apoio ?? item.nivelApoio ?? item.supportLevel),
    tentativas: Number.isInteger(attempts) && attempts > 0 ? attempts : null,
    adaptacao: optionalText(item.adaptacao),
    contexto: optionalText(item.contexto),
    motivoInterrupcao: optionalText(item.motivoInterrupcao),
    obs: optionalText(item.obs ?? item.observacao),
    proximoPasso: optionalText(item.proximoPasso),
    generalizacao: normalizeGeneralizationContexts(item.generalizacao ?? item.contextosGeneralizacao ?? item.generalization),
    participacao: normalizeParticipation(item.participacao ?? item.participation),
  };
}

export function formatSessionResultItem(item = {}, fallbackTitle = 'Atividade') {
  const title = item.titulo || item.tituloSnapshot || fallbackTitle;
  return [
    `${title}: ${performanceLabel(item.desempenho ?? item.resultado)}`,
    supportLevelLabel(item.apoio ?? item.nivelApoio ?? item.supportLevel),
    generalizationSummary(item.generalizacao ?? item.contextosGeneralizacao ?? item.generalization),
    participationSummary(item.participacao ?? item.participation),
  ].filter(Boolean).join(' · ');
}

export function sessionResultSummary(log = {}) {
  return (Array.isArray(log.itens) ? log.itens : [])
    .map((item) => formatSessionResultItem(item))
    .join('\n');
}

export function sessionGeneralObservation(log = {}) {
  const observation = optionalText(log.observacao ?? log.observation);
  const items = Array.isArray(log.itens) ? log.itens : [];
  if (!observation || !items.length) return observation;
  const prefixes = items
    .map((item) => optionalText(item.titulo || item.tituloSnapshot))
    .filter(Boolean)
    .map((title) => `${title}:`);
  return observation
    .split('\n')
    .filter((line) => !prefixes.some((prefix) => line.trim().startsWith(prefix)))
    .join('\n')
    .trim();
}
